import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Camera } from 'lucide-react';
import { COACHING_PALMISTRY_DISCLAIMER, COACHING_PALMISTRY_TIPS } from '@mlm/shared/coaching';
import { Block, CoachButton, ErrorNote, Eyebrow, Screen } from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import { coaching, type PalmReading } from '../lib/api';
import { prepareVisionImage } from '../lib/image';
import { useFeatureLocked } from '../lib/queries';

/**
 * The palm reading.
 *
 * The photo is sent, read once and never stored: no bucket object, no row, no
 * key. The screen says so twice, before the picture is taken and under the
 * reading, because a member handing over a photograph of their hand is owed a
 * plain answer about where it goes.
 */
export function PalmistryPage() {
  const locked = useFeatureLocked('palmistry');
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const read = useMutation({
    mutationFn: async () => {
      const image = await prepareVisionImage(file!);
      const form = new FormData();
      form.append('file', image, 'palm.jpg');
      return coaching.readPalm(form);
    },
  });

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const reset = () => {
    setFile(null);
    read.reset();
    if (input.current) input.current.value = '';
  };

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Palm reading" title="Read my palm">
        <ProLock feature="palmistry" />
      </Screen>
    );
  }

  if (read.data) return <Reading reading={read.data.reading} onAgain={reset} />;

  return (
    <Screen
      back="/align"
      eyebrow="Palm reading"
      title="Read my palm"
      subtitle="Take a photo of your open hand. It is read once and is never saved."
    >
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/heic,image/heif"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => setFile(e.target.files?.[0] ?? null)}
      />

      <section className="border-b border-border px-5 py-6">
        {preview ? (
          <img
            src={preview}
            alt="The palm photo you are about to send"
            className="mb-5 max-h-80 w-full rounded-sm object-contain"
          />
        ) : (
          <div className="mb-5 flex h-48 w-full items-center justify-center border border-dashed border-[rgba(16,32,26,0.22)] bg-wash">
            <Camera className="h-8 w-8 text-sage" aria-hidden />
          </div>
        )}
        <CoachButton onClick={() => input.current?.click()}>
          {file ? 'Choose a different photo' : 'Take or choose a photo'}
        </CoachButton>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-4">For a reading that works</Eyebrow>
        <ul className="space-y-3">
          {COACHING_PALMISTRY_TIPS.map((tip) => (
            <li key={tip} className="flex gap-3.5">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-verde" aria-hidden />
              <span className="flex-1 text-[0.95rem] leading-relaxed text-ink/70">{tip}</span>
            </li>
          ))}
        </ul>
      </section>

      {read.isError && <ErrorNote error={read.error} retry={() => read.mutate()} />}

      <section className="border-b border-border px-5 py-6">
        <CoachButton kind="gold" disabled={!file || read.isPending} onClick={() => read.mutate()}>
          {read.isPending ? 'Reading…' : 'Read my palm'}
        </CoachButton>
      </section>

      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {COACHING_PALMISTRY_DISCLAIMER}
      </p>
    </Screen>
  );
}

function Reading({ reading, onAgain }: { reading: PalmReading; onAgain: () => void }) {
  if (!reading.readable) {
    return (
      <Screen back="/align" eyebrow="Palm reading" title="That one did not read">
        <Block action={<CoachButton onClick={onAgain}>Try another photo</CoachButton>}>
          {reading.overview}
        </Block>
        <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
          {reading.disclaimer}
        </p>
      </Screen>
    );
  }

  const sections: Array<[string, string]> = [
    ['Life line', reading.lines.life],
    ['Head line', reading.lines.head],
    ['Heart line', reading.lines.heart],
    ['Fate line', reading.lines.fate],
  ];

  return (
    <Screen
      back="/align"
      eyebrow="Palm reading"
      title={reading.handType}
      subtitle={reading.overview}
    >
      <section className="space-y-6 border-b border-border px-5 py-6">
        {sections.map(([label, text]) => (
          <div key={label}>
            <Eyebrow>{label}</Eyebrow>
            <p className="mt-1.5 text-[0.95rem] leading-relaxed text-ink/80">{text}</p>
          </div>
        ))}
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-2">The mounts</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{reading.mounts}</p>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-2">Markings</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{reading.markings}</p>
      </section>

      <Block eyebrow="One thing to take away">{reading.guidance}</Block>

      <section className="border-b border-border px-5 py-6">
        <CoachButton onClick={onAgain}>Read another photo</CoachButton>
        <p className="eyebrow mt-4">
          Keep this reading now if you want it. It is not saved anywhere.
        </p>
      </section>

      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {reading.disclaimer}
      </p>
    </Screen>
  );
}
